import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  BookOpen,
  GraduationCap,
  Download,
  Megaphone,
  Sparkles,
  Send,
  Settings,
  ChevronsLeft,
  ChevronsRight,
  Shield,
  UserPlus,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import TopBar from "./TopBar";

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const NAV = [
    { to: "/", label: t("nav.dashboard"), testKey: "dashboard", icon: LayoutDashboard, end: true },
    { to: "/knowledge-base", label: t("nav.knowledgeBase"), testKey: "knowledge-base", icon: BookOpen },
    { to: "/academy", label: t("nav.academy"), testKey: "academy", icon: GraduationCap },
    { to: "/downloads", label: t("nav.downloads"), testKey: "downloads", icon: Download },
    { to: "/announcements", label: t("nav.announcements"), testKey: "announcements", icon: Megaphone },
    { to: "/telegram", label: t("nav.telegram"), testKey: "telegram", icon: Send },
    { to: "/invite", label: t("nav.invite"), testKey: "invite", icon: UserPlus },
    { to: "/ai-assistant", label: t("nav.emAi"), testKey: "em-ai", icon: Sparkles },
    { to: "/settings", label: t("nav.settings"), testKey: "settings", icon: Settings },
  ];

  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile drawer open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [mobileOpen]);

  return (
    <div className="min-h-screen flex bg-ink-0 text-white relative z-10">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-ink-0/70 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 h-screen border-r border-white/5 bg-ink-0 flex flex-col z-40 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{
          width: collapsed ? 80 : 260,
          transition: "width 220ms cubic-bezier(0.2,0.7,0.2,1), transform 220ms cubic-bezier(0.2,0.7,0.2,1)",
        }}
      >
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-white/5">
          {collapsed ? (
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gold text-ink-0 font-display font-bold">
              E
            </div>
          ) : (
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="font-display text-xl font-semibold tracking-tight text-white">EM</span>
              <span className="font-display text-sm font-light tracking-tight text-gold truncate">Operating System</span>
            </div>
          )}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-zinc-500 hover:text-white p-1"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-6 px-3 space-y-0.5 overflow-y-auto">
          {!collapsed && (
            <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.24em] text-zinc-600 font-semibold">{t("common.workspace")}</div>
          )}
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.testKey}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive
                    ? "bg-white/5 text-gold border-l-2 border-gold pl-[10px]"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
              style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}

          {user?.role === "admin" && (
            <>
              {!collapsed && (
                <div className="px-3 pt-6 pb-2 text-[10px] uppercase tracking-[0.24em] text-zinc-600 font-semibold flex items-center gap-1.5">
                  <Shield className="w-3 h-3" /> {t("common.admin")}
                </div>
              )}
              <NavLink
                to="/admin/knowledge"
                data-testid="nav-admin-panel"
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive
                      ? "bg-white/5 text-gold border-l-2 border-gold pl-[10px]"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`
                }
                style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
              >
                <Shield className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
                {!collapsed && <span className="truncate">{t("nav.adminPanel")}</span>}
              </NavLink>
            </>
          )}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="border-t border-white/5 p-3 hidden lg:block">
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed((v) => !v)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 text-sm"
            style={{ transitionProperty: "color, background-color", transitionDuration: "180ms" }}
            aria-label="Toggle sidebar"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : (
              <>
                <ChevronsLeft className="w-4 h-4" />
                <span className="text-xs">{t("common.collapse")}</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main
        className="flex-1 min-h-screen w-full"
        style={{
          marginLeft: 0,
          paddingLeft: 0,
          transition: "margin-left 220ms cubic-bezier(0.2,0.7,0.2,1)",
        }}
      >
        <div
          className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10"
          style={{ marginLeft: "auto", marginRight: "auto" }}
        >
          <TopBar onOpenMobileMenu={() => setMobileOpen(true)} />
          <div className="py-8 lg:py-10 animate-fade-in">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Desktop: apply sidebar offset via a spacer approach using CSS */}
      <style>{`
        @media (min-width: 1024px) {
          main {
            margin-left: ${collapsed ? 80 : 260}px !important;
          }
        }
      `}</style>
    </div>
  );
}
